import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { WHATSAPP_URL } from "@/data/services";

const LINKS = [
  ["Layanan", "#layanan"],
  ["Karya", "#karya"],
  ["Cara Kerja", "#cara-kerja"],
  ["URL Hidup", "#url-hidup"],
  ["Kontak", "#kontak"],
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,border-color] duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-[#0B132B]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-[#0B132B]/45 backdrop-blur-md"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <a
          href="#top"
          onClick={(e) => go(e, "#top")}
          className="flex items-baseline gap-2"
          data-testid="nav-logo"
        >
          <span className="font-display text-2xl text-[#F6F0E6]">Ihtifaal</span>
          <span className="font-mono text-[10px] tracking-[0.3em] text-[#C9A227]">
            احتفال
          </span>
        </a>
        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              onClick={(e) => go(e, href)}
              data-magnetic
              data-testid={`nav-link-${label.toLowerCase().replace(" ", "-")}`}
              className="text-sm text-[#F6F0E6]/70 transition-colors duration-200 hover:text-[#E8C98A]"
            >
              {label}
            </a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-magnetic
            data-testid="nav-cta-whatsapp"
            className="hidden items-center gap-2 rounded-full bg-[#C9A227] px-5 py-2.5 text-sm font-semibold text-[#141318] transition-shadow duration-200 hover:shadow-[0_8px_30px_rgba(201,162,39,0.4)] sm:inline-flex"
          >
            <MessageCircle size={16} />
            Konsultasi
          </a>
          <button
            className="text-[#F6F0E6] lg:hidden"
            onClick={() => setOpen((o) => !o)}
            aria-label="Buka menu"
            data-testid="nav-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-b border-white/10 bg-[#0B132B]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  onClick={(e) => go(e, href)}
                  data-testid={`nav-mobile-${label
                    .toLowerCase()
                    .replace(" ", "-")}`}
                  className="rounded-lg px-3 py-3 text-[#F6F0E6]/80 transition-colors hover:bg-white/5"
                >
                  {label}
                </a>
              ))}
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-cta"
                className="mt-2 rounded-full bg-[#C9A227] px-5 py-3 text-center font-semibold text-[#141318]"
              >
                Konsultasi via WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
